import {
    DepositConfig,
    FeeSponsorshipConfig,
    HallidayPaymentsConfig,
    PaymentsWidgetSDKParams,
    WithdrawalConfig,
} from "../types";

/**
 * The flat engine params (`PaymentsWidgetSDKParams`) plus the class-level passthrough knobs
 * (`instanceId`/`autoPreload`/`dangerousOverrides`) that ride alongside but are consumed by the
 * `HallidayPayments` class rather than fed straight to the widget URL.
 */
export type NormalizedConfig = Partial<PaymentsWidgetSDKParams> &
    Partial<Pick<HallidayPaymentsConfig, "instanceId" | "autoPreload" | "dangerousOverrides">>;

// Shared, non-grouped keys copied verbatim from the public config (presence-based, so explicit null clears).
const TOP_LEVEL_KEYS = [
    "apiKey",
    "sandbox",
    "owner",
    "onConnectWallet",
    "customStyles",
    "fontName",
    "headerTitle",
    "targetElementId",
    "instanceId",
    "autoPreload",
    "dangerousOverrides",
] as const satisfies readonly (keyof HallidayPaymentsConfig)[];

// Nested→flat NAME mapping, verified against BOTH the nested and flat types: renaming a flat param on
// PaymentsWidgetSDKParams (or removing/adding a nested key) fails typecheck right here at the `satisfies`.
const DEPOSIT_MAP = {
    outputs: "outputs",
    inputs: "inputs",
    funders: "funders",
    destinationAddress: "destinationAddress",
    defaultTab: "defaultLandingTab",
    tags: "depositTags",
} as const satisfies Record<keyof DepositConfig, keyof PaymentsWidgetSDKParams>;

const WITHDRAWAL_MAP = {
    inputs: "withdrawInputs",
    funder: "withdrawFunder",
    destinationAddress: "withdrawDestinationAddress",
    tags: "withdrawTags",
    // `outputs` deliberately ABSENT → dropped. There is no flat withdrawal-outputs target today; its absence
    // is now visible and intentional (the Partial<Record<...>> anchor permits omitting it).
} as const satisfies Partial<Record<keyof WithdrawalConfig, keyof PaymentsWidgetSDKParams>>;

// feeSponsorship's sub-keys are COUPLED (unlike deposit's independent ones), but the flatten is identical:
// name-map anchored to BOTH types so renaming a flat param or a nested key fails typecheck right here.
const FEE_SPONSORSHIP_MAP = {
    profileName: "profileName",
    generateGrant: "generateFeeSponsorshipGrant",
} as const satisfies Record<keyof FeeSponsorshipConfig, keyof PaymentsWidgetSDKParams>;

/**
 * Pure nested→flat config normalizer. Mirrors the pure-function style of `state.ts`.
 *
 * Maps the public bifurcated config (`deposit`/`withdrawal` groups + shared top-level keys) onto the flat
 * engine params consumed by the pipeline.
 *
 * Invariants:
 * - Emits ONLY keys present in the input partial — a partial update stays a sparse flat partial, so the
 *   downstream shallow `mergeParams` never clobbers untouched keys.
 * - Carries an explicit `null` through unchanged (used to clear a value on `updateConfig`).
 * - NEVER emits a flat target for `withdrawal.outputs` — there is no flat withdrawal target today (deferred).
 *   That drop is expressed by the deliberate absence of an `outputs` entry in `WITHDRAWAL_MAP`.
 * - Does NOT apply the `deposit.outputs` default — that is baked once in the constructor; applying it here
 *   would re-inject it on every `updateConfig` and clobber a user-set value.
 */
export const normalizeConfig = (nested: Partial<HallidayPaymentsConfig>): NormalizedConfig => {
    const out: NormalizedConfig = {};
    // Presence-based copy carries a runtime `null` (the clear signal) and `undefined` verbatim, which the
    // static optional field types don't model; the flat KEY names are still compiler-checked via the
    // `satisfies` anchors/maps above.
    const write = out as Record<string, unknown>;

    // Shared top-level keys pass through verbatim (presence-based, so explicit null is carried through).
    for (const key of TOP_LEVEL_KEYS) {
        if (key in nested) write[key] = nested[key];
    }

    // deposit group → flat params, driven off DEPOSIT_MAP.
    if (nested.deposit != null) {
        const deposit = nested.deposit;
        for (const [nestedKey, flatName] of Object.entries(DEPOSIT_MAP) as [
            keyof DepositConfig,
            keyof PaymentsWidgetSDKParams,
        ][]) {
            if (nestedKey in deposit) write[flatName] = deposit[nestedKey];
        }
    }

    // withdrawal group → flat params, driven off WITHDRAWAL_MAP (its missing `outputs` entry drops
    // `withdrawal.outputs`).
    if (nested.withdrawal != null) {
        const withdrawal = nested.withdrawal;
        for (const [nestedKey, flatName] of Object.entries(WITHDRAWAL_MAP) as [
            keyof WithdrawalConfig,
            keyof PaymentsWidgetSDKParams,
        ][]) {
            if (nestedKey in withdrawal) write[flatName] = withdrawal[nestedKey];
        }
    }

    // feeSponsorship group → flat params, driven off FEE_SPONSORSHIP_MAP. Present-keys-only (like deposit),
    // so a partial update (e.g. a lone `profileName` rename) never clobbers the untouched signer, and an
    // explicit clear object (`{ profileName: null, generateGrant: undefined }`) carries the null/undefined
    // through to flip the flat signer's presence + clear the flat `profileName`.
    if (nested.feeSponsorship != null) {
        const feeSponsorship = nested.feeSponsorship;
        for (const [nestedKey, flatName] of Object.entries(FEE_SPONSORSHIP_MAP) as [
            keyof FeeSponsorshipConfig,
            keyof PaymentsWidgetSDKParams,
        ][]) {
            if (nestedKey in feeSponsorship) write[flatName] = feeSponsorship[nestedKey];
        }
    }

    return out;
};
